import { AppLayout } from "@/components/AppLayout";
import { Receipt, Download, CreditCard, Calendar, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const invoices = [
  {
    id: "INV-2024-001",
    date: "1 décembre 2024",
    amount: "24€",
    status: "Payée",
    description: "Pack 300 crédits",
  },
  {
    id: "INV-2024-002",
    date: "15 novembre 2024",
    amount: "9€",
    status: "Payée",
    description: "Pack 100 crédits",
  },
  {
    id: "INV-2024-003",
    date: "1 novembre 2024",
    amount: "24€",
    status: "Payée",
    description: "Pack 300 crédits",
  },
  {
    id: "INV-2024-004",
    date: "15 octobre 2024",
    amount: "39€",
    status: "Payée",
    description: "Pack 500 crédits",
  },
];

const Billing = () => {
  const { toast } = useToast();

  const handleDownload = (invoiceId: string) => {
    toast({
      title: "Téléchargement lancé",
      description: `Facture ${invoiceId} en cours de téléchargement...`,
    });
  };

  const handleDownloadAll = () => {
    toast({
      title: "Téléchargement lancé",
      description: "Toutes vos factures sont en cours de téléchargement...",
    });
  };

  return (
    <AppLayout title="Facturation">
      <div className="p-6 space-y-8 max-w-4xl">
        {/* Summary */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Total dépensé</span>
            </div>
            <div className="text-3xl font-bold text-foreground">96€</div>
            <span className="text-sm text-muted-foreground">cette année</span>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-5 h-5 text-highlight" />
              <span className="text-sm text-muted-foreground">Factures</span>
            </div>
            <div className="text-3xl font-bold text-foreground">{invoices.length}</div>
            <span className="text-sm text-muted-foreground">au total</span>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-accent" />
              <span className="text-sm text-muted-foreground">Dernier achat</span>
            </div>
            <div className="text-lg font-bold text-foreground">1 déc. 2024</div>
            <span className="text-sm text-muted-foreground">Pack 300 crédits</span>
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Mode de paiement</h2>
          <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center text-white text-xs font-bold">
                VISA
              </div>
              <div>
                <div className="font-medium text-foreground">•••• •••• •••• 4242</div>
                <div className="text-sm text-muted-foreground">Expire 12/26</div>
              </div>
            </div>
            <Button variant="outline" size="sm" className="rounded-xl">
              Modifier
            </Button>
          </div>
          <Button variant="outline" className="rounded-xl">
            <CreditCard className="w-4 h-4 mr-2" />
            Ajouter un mode de paiement
          </Button>
        </div>

        {/* Invoices */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold text-foreground">Historique des factures</h2>
            </div>
            <Button variant="outline" onClick={handleDownloadAll} className="rounded-xl">
              <Download className="w-4 h-4 mr-2" />
              Tout télécharger
            </Button>
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Facture</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Description</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Montant</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Statut</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-border last:border-0">
                    <td className="p-4 font-medium text-foreground">{invoice.id}</td>
                    <td className="p-4 text-muted-foreground">{invoice.date}</td>
                    <td className="p-4 text-muted-foreground">{invoice.description}</td>
                    <td className="p-4 font-medium text-foreground">{invoice.amount}</td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-success/20 text-success text-xs font-medium rounded-full">
                        {invoice.status}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(invoice.id)}
                        className="rounded-xl"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Billing;
